import { useCallback, useEffect, useRef, useState } from 'react';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import {
  X,
  Cloud as CloudIcon,
  Upload,
  FileText,
  Search,
  Lock,
  LogIn,
  ArrowRight,
  Loader2,
  HardDrive,
  Check,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useCloud } from '@/hooks/useCloud';
import { fetchCloudDocumentAsFile } from '@/lib/cloudApi';
import { getCloudState, getCloudUpsell } from '@/lib/cloudUpsell';
import { formatBytes } from '@/lib/billing';

/**
 * FileOpenDialog — modal with Device + Cloud tabs. Used everywhere the app
 * asks the user to pick a PDF. The native OS picker lives inside the Device
 * tab as a "Browse device…" button — one extra click, but it unlocks cloud
 * as a first-class source in the same flow.
 *
 * Resolves via `onPicked({ name, buffer, path, cloudDocId? })` or `onCancel()`.
 * The parent (FileDialogProvider) converts that into the Promise that
 * useFileDialog.openFile() resolves.
 */
export default function FileOpenDialog({
  open,
  accept = '.pdf',
  multiple = false,
  initialTab = null,   // optional 'device' | 'cloud' hint from caller
  onPicked,
  onCancel,
}) {
  const navigate = useNavigate();
  const { user, account } = useAuth();
  const cloudState = getCloudState(user, account);
  const cloudUpsell = getCloudUpsell(account);
  // Auto-load the cloud list when the dialog opens and cloud is available.
  const { documents, loading } = useCloud({ autoLoad: open && cloudState === 'available' });

  // Default tab — Cloud if the user has it, Device otherwise. Pre-selecting
  // Cloud drops users straight into their synced files, which is the payoff
  // that makes the subscription feel useful from the first interaction.
  const [tab, setTab] = useState('device');
  const [query, setQuery] = useState('');
  const [opening, setOpening] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const nativeInputRef = useRef(null);

  // Cloud only stores PDFs. If the caller wants a different accept type
  // (e.g. Batch picking images to convert), hide the Cloud tab entirely —
  // showing PDF-only contents would be misleading.
  const cloudTabVisible = typeof accept === 'string' && accept.toLowerCase().includes('.pdf');

  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIds(new Set());
      // Respect caller's initialTab hint when it's a valid + available choice.
      // Callers that want Cloud specifically (e.g. the "From cloud" buttons on
      // tool pages) pass initialTab='cloud'; falls back to the default
      // (Cloud if available, Device otherwise) when no hint given.
      const requestedCloud = initialTab === 'cloud' && cloudTabVisible;
      const requestedDevice = initialTab === 'device';
      const fallback = (cloudTabVisible && cloudState === 'available') ? 'cloud' : 'device';
      setTab(requestedCloud ? 'cloud' : requestedDevice ? 'device' : fallback);
    }
  }, [open, cloudState, cloudTabVisible, initialTab]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onEscape = (e) => { if (e.key === 'Escape') onCancel?.(); };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [open, onCancel]);

  const handleBrowseDevice = useCallback(() => {
    nativeInputRef.current?.click();
  }, []);

  const handleNativeChange = useCallback(async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // allow reselecting the same file later
    if (files.length === 0) return;
    if (multiple) {
      const results = await Promise.all(files.map(async (f) => ({
        name: f.name,
        buffer: await f.arrayBuffer(),
        path: null,
      })));
      onPicked?.(results);
    } else {
      const f = files[0];
      onPicked?.({ name: f.name, buffer: await f.arrayBuffer(), path: null });
    }
  }, [multiple, onPicked]);

  // Single-click open (multiple=false): fetch and resolve immediately.
  const handleOpenCloudDoc = useCallback(async (doc) => {
    setOpening(doc.id);
    try {
      const file = await fetchCloudDocumentAsFile(doc.id);
      const buffer = await file.arrayBuffer();
      onPicked?.({ name: file.name, buffer, path: null, cloudDocId: doc.id });
    } catch (err) {
      console.error('Open cloud doc failed:', err);
    } finally {
      setOpening(null);
    }
  }, [onPicked]);

  // Checkbox toggle (multiple=true). Adds/removes an id from selectedIds
  // without fetching — the fetch happens on "Open N files" confirm.
  const toggleSelected = useCallback((docId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId); else next.add(docId);
      return next;
    });
  }, []);

  // Bulk-open confirm. Fetches each selected doc in parallel (small numbers
  // typical for merge; we can add pagination later if users routinely pick
  // more than ~10), resolves with an array so the useFileDialog wrapper
  // doesn't have to normalize it.
  const handleOpenSelectedCloudDocs = useCallback(async () => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    setOpening('__bulk__');
    try {
      const picked = await Promise.all(ids.map(async (id) => {
        const file = await fetchCloudDocumentAsFile(id);
        const buffer = await file.arrayBuffer();
        return { name: file.name, buffer, path: null, cloudDocId: id };
      }));
      onPicked?.(picked);
    } catch (err) {
      console.error('Open selected cloud docs failed:', err);
    } finally {
      setOpening(null);
    }
  }, [selectedIds, onPicked]);

  if (!open) return null;

  const filteredDocs = (documents || []).filter((d) => {
    if (!query) return true;
    return d.filename.toLowerCase().includes(query.toLowerCase());
  });

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-4" onClick={onCancel}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[560px] bg-white rounded-2xl shadow-2xl overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-label="Open PDF"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <FileText size={18} className="text-brand-blue" />
            <div className="text-sm font-semibold">Open PDF</div>
          </div>
          <button onClick={onCancel} className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500" aria-label="Close">
            <X size={14} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-5">
          <TabButton active={tab === 'device'} onClick={() => setTab('device')}>
            <HardDrive size={14} />
            Device
          </TabButton>
          {cloudTabVisible && (
            <TabButton active={tab === 'cloud'} onClick={() => setTab('cloud')}>
              <CloudIcon size={14} />
              Cloud
              {cloudState === 'available' && documents && documents.length > 0 && (
                <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${tab === 'cloud' ? 'bg-brand-light text-brand-blue' : 'bg-gray-100 text-gray-500'}`}>
                  {documents.length}
                </span>
              )}
              {cloudState !== 'available' && (
                <span className="ml-1 w-4 h-4 rounded-full bg-gray-200 text-gray-500 inline-flex items-center justify-center">
                  {cloudState === 'locked' ? <Lock size={9} /> : <LogIn size={9} />}
                </span>
              )}
            </TabButton>
          )}
        </div>

        {/* Hidden native input — "Browse device…" triggers it */}
        <input
          ref={nativeInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleNativeChange}
          className="hidden"
        />

        {/* Body */}
        <div className="p-5 min-h-[280px]">
          {tab === 'device' && (
            <DevicePane accept={accept} onBrowse={handleBrowseDevice} />
          )}

          {tab === 'cloud' && cloudState === 'available' && (
            <CloudPane
              loading={loading}
              documents={filteredDocs}
              totalCount={documents?.length || 0}
              query={query}
              setQuery={setQuery}
              opening={opening}
              multiple={multiple}
              selectedIds={selectedIds}
              onToggleSelected={toggleSelected}
              onOpen={handleOpenCloudDoc}
              onOpenSelected={handleOpenSelectedCloudDocs}
              onManage={() => { onCancel?.(); navigate('/cloud'); }}
            />
          )}

          {tab === 'cloud' && cloudState === 'locked' && (
            <LockedPane
              upsell={cloudUpsell}
              onUpgrade={() => { onCancel?.(); navigate('/account/billing?highlight=cloud'); }}
              onUseDevice={() => setTab('device')}
            />
          )}

          {tab === 'cloud' && cloudState === 'signed-out' && (
            <SignedOutPane
              onSignIn={() => { onCancel?.(); navigate('/login?next=/tools'); }}
              onUseDevice={() => setTab('device')}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Pane subcomponents ─────────────────────────────────────────────────────

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2.5 text-sm border-b-2 flex items-center gap-1.5 transition-colors ${
        active
          ? 'border-brand-blue text-brand-blue font-semibold'
          : 'border-transparent text-gray-500 hover:text-gray-800 font-medium'
      }`}
    >
      {children}
    </button>
  );
}

function DevicePane({ accept, onBrowse }) {
  const acceptLabel = accept === '.pdf' ? 'PDFs' : accept;
  return (
    <button
      onClick={onBrowse}
      className="w-full border-2 border-dashed border-gray-300 rounded-xl p-10 text-center hover:border-brand-blue hover:bg-brand-light/30 transition-colors cursor-pointer"
    >
      <div className="w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center bg-brand-light text-brand-blue">
        <Upload size={22} />
      </div>
      <div className="text-sm font-semibold">Browse device…</div>
      <div className="text-xs text-gray-500 mt-1">Pick {acceptLabel} from your computer</div>
    </button>
  );
}

function CloudPane({
  loading, documents, totalCount, query, setQuery, opening,
  multiple = false, selectedIds = new Set(), onToggleSelected,
  onOpen, onOpenSelected, onManage,
}) {
  const selectedCount = selectedIds.size;
  const bulkPending = opening === '__bulk__';

  return (
    <>
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search your cloud…"
            className="w-full pl-9 pr-3 h-9 text-sm border border-gray-200 rounded-lg focus:border-brand-blue focus:outline-none"
          />
        </div>
      </div>

      {loading ? (
        <div className="py-10 text-center text-gray-400 text-xs flex items-center justify-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading your cloud…
        </div>
      ) : documents.length === 0 ? (
        <div className="py-10 text-center text-gray-500 text-xs">
          {totalCount === 0 ? (
            <>
              No cloud documents yet.
              <button onClick={onManage} className="ml-1 text-brand-blue font-semibold hover:underline">Upload one →</button>
            </>
          ) : 'No matches.'}
        </div>
      ) : (
        <ul className="space-y-1 max-h-[320px] overflow-y-auto">
          {documents.map((doc) => {
            const isSelected = selectedIds.has(doc.id);
            const isFetching = opening === doc.id;
            if (multiple) {
              // Checkbox row — clicking anywhere toggles. Fetch happens on
              // the "Open N" button at the footer, not per click.
              return (
                <li key={doc.id}>
                  <label className={`w-full flex items-center gap-3 p-2 rounded-lg text-left cursor-pointer transition-colors ${
                    isSelected ? 'bg-brand-light/60' : 'hover:bg-gray-50'
                  }`}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onToggleSelected?.(doc.id)}
                      disabled={bulkPending}
                      className="w-4 h-4 rounded border-gray-300 text-brand-blue focus:ring-brand-blue focus:ring-offset-0"
                    />
                    <div className="w-8 h-10 rounded bg-white border border-gray-200 flex items-center justify-center shrink-0">
                      <FileText size={14} className="text-accent-coral" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{doc.filename}</div>
                      <div className="text-[11px] text-gray-500">
                        {formatBytes(doc.fileSizeBytes)}
                        {doc.pageCount > 0 && ` · ${doc.pageCount} ${doc.pageCount === 1 ? 'page' : 'pages'}`}
                      </div>
                    </div>
                  </label>
                </li>
              );
            }
            return (
              <li key={doc.id}>
                <button
                  onClick={() => onOpen(doc)}
                  disabled={isFetching}
                  className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 text-left disabled:opacity-60"
                >
                  <div className="w-8 h-10 rounded bg-white border border-gray-200 flex items-center justify-center shrink-0">
                    {isFetching
                      ? <Loader2 size={14} className="text-brand-blue animate-spin" />
                      : <FileText size={14} className="text-accent-coral" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{doc.filename}</div>
                    <div className="text-[11px] text-gray-500">
                      {formatBytes(doc.fileSizeBytes)}
                      {doc.pageCount > 0 && ` · ${doc.pageCount} ${doc.pageCount === 1 ? 'page' : 'pages'}`}
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1 text-[10px] text-accent-green font-semibold">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent-green"></span> Synced
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-[11px] text-gray-500 gap-3">
        <span>
          {multiple && selectedCount > 0
            ? `${selectedCount} selected · ${totalCount} total`
            : `${totalCount} ${totalCount === 1 ? 'file' : 'files'}`}
        </span>
        {multiple ? (
          <div className="flex items-center gap-3">
            <button onClick={onManage} className="text-brand-blue font-semibold hover:underline">Manage cloud →</button>
            <button
              onClick={onOpenSelected}
              disabled={selectedCount === 0 || bulkPending}
              className="inline-flex items-center gap-1.5 px-3 h-8 rounded-lg text-xs font-semibold text-white hover:opacity-95 disabled:opacity-40 transition-opacity"
              style={{ background: 'linear-gradient(135deg, #4F46E5, #6366F1)' }}
            >
              {bulkPending
                ? <><Loader2 size={12} className="animate-spin" /> Opening…</>
                : selectedCount === 0
                  ? 'Select files'
                  : `Open ${selectedCount} ${selectedCount === 1 ? 'file' : 'files'}`}
            </button>
          </div>
        ) : (
          <button onClick={onManage} className="text-brand-blue font-semibold hover:underline">Manage cloud →</button>
        )}
      </div>
    </>
  );
}

function LockedPane({ upsell, onUpgrade, onUseDevice }) {
  return (
    <>
      <div className="rounded-xl p-6 bg-gradient-to-br from-brand-light to-white border border-brand-light relative overflow-hidden">
        <div
          className="absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-30"
          style={{ background: 'radial-gradient(circle, #A5B4FC 0%, transparent 70%)' }}
        />
        <div className="relative">
          <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center text-brand-blue mb-3">
            <CloudIcon size={22} />
          </div>
          <div className="text-base font-bold">Access your files from any device</div>
          <p className="text-xs text-gray-600 mt-1 max-w-sm">
            Cloud storage lets you upload PDFs once and open them from any signed-in device — auto-sync, version history, shareable links.
          </p>
          <ul className="mt-3 space-y-1">
            <li className="flex items-center gap-2 text-[11px] text-gray-700"><Check size={11} className="text-accent-green" /> Sync across devices</li>
            <li className="flex items-center gap-2 text-[11px] text-gray-700"><Check size={11} className="text-accent-green" /> 30-day version history</li>
            <li className="flex items-center gap-2 text-[11px] text-gray-700"><Check size={11} className="text-accent-green" /> Share view / edit links</li>
          </ul>
          {upsell.canBuy && (
            <button
              onClick={onUpgrade}
              className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white hover:opacity-95 transition-opacity"
              style={{ background: 'linear-gradient(135deg, #4F46E5, #6366F1)' }}
            >
              {upsell.ctaLong}
              <ArrowRight size={12} />
            </button>
          )}
        </div>
      </div>
      <div className="mt-4 text-[11px] text-gray-500 text-center">
        Just need to open a file?{' '}
        <button onClick={onUseDevice} className="text-brand-blue font-semibold hover:underline">
          Use Device →
        </button>
      </div>
    </>
  );
}

function SignedOutPane({ onSignIn, onUseDevice }) {
  return (
    <>
      <div className="rounded-xl p-6 bg-gradient-to-br from-brand-light to-white border border-brand-light">
        <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center text-brand-blue mb-3">
          <CloudIcon size={22} />
        </div>
        <div className="text-base font-bold">Sign in to use Scrivar Cloud</div>
        <p className="text-xs text-gray-600 mt-1 max-w-sm">
          Save your PDFs to the cloud and open them on any signed-in device.
        </p>
        <button
          onClick={onSignIn}
          className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white hover:opacity-95 transition-opacity"
          style={{ background: 'linear-gradient(135deg, #4F46E5, #6366F1)' }}
        >
          <LogIn size={14} />
          Sign in
        </button>
      </div>
      <div className="mt-4 text-[11px] text-gray-500 text-center">
        Just need to open a file?{' '}
        <button onClick={onUseDevice} className="text-brand-blue font-semibold hover:underline">
          Use Device →
        </button>
      </div>
    </>
  );
}
